/**
 * The period board: one row per borrower, held on screen while it refetches.
 *
 * Two behaviours the plain "fetch on mount" shape does not give:
 *
 * - **Polling while work is in flight.** Generation is queued on the server, so
 *   a row that says "Generating…" only stops saying it because a later board
 *   says so. The poll runs ONLY while some row is generating and stops when
 *   none is — a board of finished runs is a static page and must not keep a
 *   timer alive behind it.
 * - **Hold and dim.** A refetch of the SAME period is answering the question
 *   already on screen, so the rows stay and `stale` asks the caller to dim
 *   them (`useKpiTable`'s contract). Clearing the table for every poll would
 *   flash the board every five seconds.
 *
 * A poll that fails does not blank the board: the rows on screen are the last
 * true answer, and a transient 502 mid-generation is not a reason to lose them.
 * A failed FIRST load has nothing to hold, so it surfaces as `error`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { monitoringService } from "../services/monitoringService";
import type { MonitoringBoard } from "../types/monitoring";
import { isRateLimited } from "../utils/isRateLimited";

/** Slow enough not to hammer the API, fast enough that a run "just finishes". */
export const BOARD_POLL_MS = 5000;

export interface MonitoringBoardData {
	board: MonitoringBoard | null;
	loading: boolean;
	/** A refetch is in flight and the rows on screen are being held. */
	stale: boolean;
	error: Error | undefined;
	refresh: () => void;
}

/** True while the server still owes this board a run it has not finished. */
export const isBoardGenerating = (board: MonitoringBoard | null): boolean =>
	board !== null && board.rows.some((row) => row.run?.generating === true);

export function useMonitoringBoard(
	periodId: string | null | undefined,
): MonitoringBoardData {
	const [board, setBoard] = useState<MonitoringBoard | null>(null);
	const [loading, setLoading] = useState(false);
	const [stale, setStale] = useState(false);
	const [error, setError] = useState<Error | undefined>(undefined);
	// A 429 stops the poll. Polling into a spent budget only keeps it spent;
	// the next read that succeeds (a manual refresh, a write's re-read) resumes it.
	const [rateLimited, setRateLimited] = useState(false);

	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	// Rising counter: switching periods quickly must not let the previous
	// period's rows land under the new period's heading.
	const requestId = useRef(0);

	const load = useCallback(
		async (mode: "initial" | "refresh") => {
			if (!periodId) return;
			const id = ++requestId.current;
			if (mode === "initial") {
				setLoading(true);
				setBoard(null);
				setError(undefined);
			} else {
				setStale(true);
			}
			try {
				const next = await monitoringService.getBoard(periodId);
				if (!mounted.current || id !== requestId.current) return;
				setBoard(next);
				setError(undefined);
				setRateLimited(false);
			} catch (caught) {
				if (!mounted.current || id !== requestId.current) return;
				if (isRateLimited(caught)) setRateLimited(true);
				// Only the first load may leave the page with an error and no rows.
				// A poll that failed still has the last good board on screen.
				if (mode === "initial") {
					setError(caught instanceof Error ? caught : new Error(String(caught)));
				}
			} finally {
				if (mounted.current && id === requestId.current) {
					setLoading(false);
					setStale(false);
				}
			}
		},
		[periodId],
	);

	useEffect(() => {
		if (!periodId) {
			setBoard(null);
			setLoading(false);
			setError(undefined);
			return;
		}
		void load("initial");
	}, [periodId, load]);

	// The poll depends on `generating`, not on the board itself, so an
	// unchanged board arriving every tick does not tear down and rebuild the
	// interval. When nothing is generating this effect registers no timer at all.
	const generating = isBoardGenerating(board);
	useEffect(() => {
		if (!periodId || !generating || rateLimited) return undefined;
		const timer = window.setInterval(() => {
			void load("refresh");
		}, BOARD_POLL_MS);
		return () => window.clearInterval(timer);
	}, [periodId, generating, rateLimited, load]);

	return useMemo(
		() => ({
			board,
			loading,
			stale,
			error,
			refresh: () => void load(board === null ? "initial" : "refresh"),
		}),
		[board, loading, stale, error, load],
	);
}
